/**
 * Labstep
 *
 * @module prosemirror/nodes/date/commands
 * @desc Date node commands
 */

import { SLASH_TRIGGER } from 'labstep-web/constants/prosemirror';
import type { IStateDispatchProps } from 'labstep-web/prosemirror/marks/types';
import { getToken } from 'labstep-web/prosemirror/utils/selection';
import { type EditorState, Transaction } from 'prosemirror-state';

const findLastDateNode = (node: any): string | null => {
  if (node.childCount) {
    for (let i = node.childCount - 1; i >= 0; i--) {
      const child = node.child(i);
      const found = findLastDateNode(child);
      if (found) {
        return found;
      }
    }
  }
  if (node.type && node.type.name === 'date' && node.attrs.date) {
    return node.attrs.date;
  }
  return null;
};

/**
 * Finds the last date node in the document and returns the next day
 * @param state - ProseMirror editor state
 * @returns The next day after the last date node, or today's date if no date nodes exist
 */
const getNextDateFromDocument = (state: EditorState): string => {
  let lastDate: string | null = null;

  // Helper function to traverse nodes in reverse order
  lastDate = findLastDateNode(state.doc);

  if (lastDate) {
    // Parse the last date and add one day
    const lastDateObj = new Date(lastDate);
    lastDateObj.setDate(lastDateObj.getDate() + 1);
    return lastDateObj.toISOString().split('T')[0]; // YYYY-MM-DD format
  }

  // If no date nodes found, return today's date
  return new Date().toISOString().split('T')[0];
};

export const insertDate = (
  state: EditorState,
  dispatch?: IStateDispatchProps['dispatch'],
): void => {
  const { schema } = state;
  const dateNodeType = schema.nodes.date;

  if (!dateNodeType) {
    return;
  }

  const token = getToken(state, SLASH_TRIGGER);
  if (!token) {
    return;
  }

  const { from, to } = token;
  const nextDate = getNextDateFromDocument(state);

  const dateNode = dateNodeType.create({
    date: nextDate,
  });

  if (dispatch) {
    const { tr } = state;

    // Replace the /date token with the date node
    tr.replaceRangeWith(from, to, dateNode);

    dispatch(tr);
  }
};
